import React from "react";

export const StackedProgressBar = ({
  data,
  segments,
  total,
  height = "h-2",
  className = "",
}) => {
  // Handle both data formats
  const barData = data || segments;
  const totalValue =
    total || barData?.reduce((sum, item) => sum + item.value, 0) || 0;

  // Return early if no data
  if (!barData || !Array.isArray(barData) || barData.length === 0) {
    return null;
  }

  let currentWidth = 0;

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Progress Bar Container */}
      <div className={`relative w-full ${height} rounded-sm bg-gray-100`}>
        {barData
          .sort((a, b) => b.value - a.value)
          .map((segment, index, array) => {
            const percentage = (segment.value / totalValue) * 100;
            const startPosition = currentWidth;
            currentWidth += percentage;

            // Determine if this is first or last segment for rounded corners
            const isFirst = index === 0;
            const isLast = index === array.length - 1;
            const roundedClass = `${isFirst ? "rounded-l-sm" : ""} ${
              isLast ? "rounded-r-sm" : ""
            }`;

            return (
              <div
                key={segment.label}
                className={`group relative h-full ${roundedClass} hover:brightness-95 transition-all`}
                style={{
                  position: "absolute",
                  left: `${startPosition}%`,
                  width: `${percentage}%`,
                  backgroundColor: segment.color,
                }}
              >
                {/* Tooltip Container */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {/* Tooltip */}
                  <div
                    className="
                      invisible group-hover:visible 
                      opacity-0 group-hover:opacity-100 
                      absolute -top-14
                      flex flex-col items-center
                      transition-all duration-200
                      z-[60]
                      pointer-events-none
                    "
                  >
                    {/* Tooltip Content */}
                    <div
                      className="
                        relative 
                        px-3 py-2
                        bg-gray-900 
                        rounded-md
                        shadow-lg
                        min-w-[120px]
                        text-center
                      "
                    >
                      <div className="text-white text-xs font-medium whitespace-nowrap">
                        <div>{segment.label}</div>
                        <div className="mt-0.5">
                          {segment.tooltipValue || segment.value} (
                          {percentage.toFixed(1)}%)
                        </div>
                      </div>

                      {/* Tooltip Arrow */}
                      <div
                        className="
                          absolute 
                          left-1/2 
                          bottom-0 
                          w-2 h-2
                          -mb-1
                          transform 
                          rotate-45 
                          bg-gray-900 
                          -translate-x-1/2
                        "
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        {barData
          .sort((a, b) => b.value - a.value)
          .map((segment) => (
            <div key={segment.label} className="flex items-center gap-2">
              <div
                className="w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: segment.color }}
              />
              <span className="text-gray-600">{segment.label}</span>
              <span className="font-medium">
                {((segment.value / totalValue) * 100).toFixed(1)}%
              </span>
            </div>
          ))}
      </div>
    </div>
  );
};
